import React, { useCallback, useEffect, useMemo, useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { formatDistance } from "date-fns";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useAllAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useAllAccounts";
import { GoalBasedFinancialPlanId } from "@fartherfinance/frontend/api/FinancialPlans/ids";
import useGetGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/useGetGoalBasedFinancialPlan";
import usePatchGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/usePatchGoalBasedFinancialPlan";
import usePostGoalBasedFinancialPlan from "@fartherfinance/frontend/api/FinancialPlans/plan/hooks/usePostGoalBasedFinancialPlan";
import { GoalBasedFinancialPlanResponse } from "@fartherfinance/frontend/api/FinancialPlans/plan/types";
import useGetGoalBasedFinancialPlanProjection, {
  goalBasedFinancialPlanProjectionKeyMaker,
} from "@fartherfinance/frontend/api/FinancialPlans/projection/hooks/useGetGoalBasedFinancialPlanProjection";
import {
  GoalBasedFinancialPlanProjectionResponse,
  GoalBasedFinancialPlanProjectionUnderwaterExceptionResponse,
} from "@fartherfinance/frontend/api/FinancialPlans/projection/types";
import {
  AltAssetId,
  ClientId,
  ExternalAccountId,
  FartherAccountId,
} from "@fartherfinance/frontend/api/Types";

import { FinancialPlansPath } from "@src/config/routing/RouterPaths";
import AlertModal from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/AlertModal/AlertModal";
import FinancialPlansDropdownMenu from "@src/multiCustodian/components/FinancialPlans/DropdownMenu";
import GoalsTable from "@src/multiCustodian/components/FinancialPlans/GoalsTable/GoalsTable";
import HeroTooltipCard from "@src/multiCustodian/components/FinancialPlans/HeroTooltipCard/HeroTooltipCard";
import FinancialPlansProjectionTable from "@src/multiCustodian/components/FinancialPlans/ProjectionTable/ProjectionTable";
import SuccessMeter from "@src/multiCustodian/components/FinancialPlans/SuccessMeter/SuccessMeter";
import BaseLayout from "@src/multiCustodian/components/Layouts/BaseLayout/BaseLayout";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import ProjectionGraph from "@src/multiCustodian/components/ProjectionGraph/Container";
import type { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import IconButton from "@src/sharedComponents/IconButton/IconButton";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Menu from "@src/sharedComponents/Menu/Menu";
import SimpleModalWithTextInput from "@src/sharedComponents/Modal/SimpleModalWithTextInput/SimpleModalWithTextInput";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import Text from "@src/sharedComponents/Typography/Text";
import { formatExternalAccountName } from "@src/utils/ExternalAccount/formatExternalAccountName";
import { getExternalAccountName } from "@src/utils/ExternalAccount/getExternalAccountName";

import styles from "./FinancialPlans.module.css";

const isProjectionUnderwaterException = (
  projectionsResponse:
    | GoalBasedFinancialPlanProjectionResponse
    | GoalBasedFinancialPlanProjectionUnderwaterExceptionResponse
): projectionsResponse is GoalBasedFinancialPlanProjectionUnderwaterExceptionResponse => {
  return "underwater_accounts" in projectionsResponse;
};

const getUpdatedAtString = (updatedAt: Date | undefined): string => {
  if (updatedAt === undefined) {
    return "";
  }

  const now = new Date();

  return `Updated: ${formatDistance(now, updatedAt)} ago`;
};

interface Props {
  completedPlans: GoalBasedFinancialPlanResponse[];
  drafts: GoalBasedFinancialPlanResponse[];
  favorite: GoalBasedFinancialPlanResponse | null;
}

const FinancialPlansDashboard: React.FC<Props> = ({
  completedPlans,
  drafts,
  favorite,
}) => {
  const { clientId, goalBasedFinancialPlanId } = useParams<{
    clientId: ClientId;
    goalBasedFinancialPlanId: GoalBasedFinancialPlanId | undefined;
  }>();
  const [showUnderwaterAccountsModal, setShowUnderwaterAccountsModal] =
    useState<boolean>(false);

  const CreateUpdatePath = `/Client/${clientId}/${FinancialPlansPath}/CreateUpdate`;

  const navigate = useNavigate();

  const notify = useStatusNotification();

  const queryClient = useQueryClient();

  const auth = useRequestAuth();

  const allAccounts = useAllAccounts(clientId, auth);

  // Track dashboard viewing
  useEffect(() => {
    trackEvent({
      name: "Client View Financial Plans Dashboard",
      properties: {
        clientId,
        totalCompletedPlans: completedPlans.length,
        totalDrafts: drafts.length,
        hasFavoritePlan: favorite !== null,
      },
    });
  }, [clientId, completedPlans.length, drafts.length, favorite]);

  useEffect(() => {
    if (goalBasedFinancialPlanId !== undefined) {
      const isInCompletedPlans = completedPlans.find(
        (cp) => cp.goalBasedFinancialPlanId === goalBasedFinancialPlanId
      );

      if (isInCompletedPlans !== undefined) {
        return;
      }

      const isInDrafts = drafts.find(
        (d) => d.goalBasedFinancialPlanId === goalBasedFinancialPlanId
      );

      if (isInDrafts === undefined) {
        navigate(`/Client/${clientId}/${FinancialPlansPath}/Dashboard`);
      }
    }
  }, [
    navigate,
    clientId,
    goalBasedFinancialPlanId,
    completedPlans,
    drafts,
    favorite,
  ]);

  const fetchProjection = useMemo(() => {
    if (goalBasedFinancialPlanId === undefined) {
      return false;
    }

    return completedPlans.find(
      (p) => p.goalBasedFinancialPlanId === goalBasedFinancialPlanId
    )
      ? true
      : false;
  }, [goalBasedFinancialPlanId, completedPlans]);

  const projection = useGetGoalBasedFinancialPlanProjection(
    fetchProjection && goalBasedFinancialPlanId !== undefined
      ? goalBasedFinancialPlanId
      : null,
    auth
  );

  const projectionQueryKey =
    goalBasedFinancialPlanId !== undefined
      ? goalBasedFinancialPlanProjectionKeyMaker(goalBasedFinancialPlanId)
      : undefined;

  const getProjectionState = projectionQueryKey
    ? queryClient.getQueryState(projectionQueryKey)
    : undefined;

  const isFetchingProjection = getProjectionState?.fetchStatus === "fetching";

  const getPlatformAccountNameAndType = useCallback(
    (
      account_id: FartherAccountId | AltAssetId | ExternalAccountId
    ): [string | undefined, string | undefined] => {
      if (allAccounts.isLoading || allAccounts.hasError) {
        return [undefined, undefined];
      }

      let accountName = undefined;
      let accountType = undefined;
      const fma = allAccounts.data.fartherAccounts.find(
        (acc) => acc.virtualAccountId === account_id
      );
      accountName = fma?.accountDetails.displayName;
      accountType = fma?.accountDetails.accountType;

      if (fma === undefined) {
        const mta = allAccounts.data.manuallyTrackedAccounts.find(
          (acc) => acc.id === account_id
        );
        accountName = mta?.assetName;
        accountType = mta?.assetType;

        if (mta === undefined) {
          const exa = allAccounts.data.externalAccounts.find(
            (acc) => acc.accountId === account_id
          );
          if (exa === undefined) {
            throw new Error(
              `Cant find account: ${account_id} in list of accounts`
            );
          }

          accountName = formatExternalAccountName(
            getExternalAccountName(exa),
            14
          );
          accountType = exa?.details.account.accountType;
        }
      }

      return [accountName, accountType];
    },
    [allAccounts]
  );

  useEffect(() => {
    if (projection.data !== undefined) {
      if (isFetchingProjection && showUnderwaterAccountsModal) {
        setShowUnderwaterAccountsModal(false);
      } else if (
        isProjectionUnderwaterException(projection.data) &&
        !showUnderwaterAccountsModal
      ) {
        setShowUnderwaterAccountsModal(true);
      }
    }
    // don't add showUnderwaterAccountsModal to useEffect deps []
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projection.data, isFetchingProjection]);

  const [
    projectionData,
    accountsData,
    probabilityScore,
    underWaterAccountsDesc,
  ] = useMemo(() => {
    if (projection.isLoading || projection.hasError) {
      return [undefined, undefined, undefined, undefined];
    }

    if (isProjectionUnderwaterException(projection.data)) {
      const underwaterAccounts = projection.data.underwater_accounts.map(
        (ua) => {
          const [accountName, accountType] = getPlatformAccountNameAndType(ua);

          return {
            accountId: ua,
            accountName: accountName ?? "N/A",
            accountType: accountType ?? "N/A",
            marketValue: undefined,
            valueAtRetirement: 0,
            valueAtEndOfPlan: 0,
          };
        }
      );

      if (underwaterAccounts.length <= 0) {
        throw new Error("Underwater accounts should not be empty");
      }

      const length = underwaterAccounts.length;
      const underwaterAccountsDesc = underwaterAccounts.reduce(
        (acc, cur, i) =>
          i === length - 1
            ? `${acc}${length > 1 ? "and " : ""}${cur.accountName}`
            : `${cur.accountName}, ${acc}`,
        ""
      );

      return [[], underwaterAccounts, 0, underwaterAccountsDesc];
    }

    const graphData = projection.data.projections.map((p) => ({
      date: p.date,
      range: [p.lower_bound, p.upper_bound] as [number, number],
      value: p.market_value,
    }));

    const tableData = projection.data.account_summary.map((a) => {
      const [accountName, accountType] = getPlatformAccountNameAndType(
        a.account_id
      );

      return {
        accountId: a.account_id,
        accountName: accountName ?? "N/A",
        accountType: accountType ?? "N/A",
        marketValue: a.current_market_value,
        valueAtRetirement: a.retirement_market_value,
        valueAtEndOfPlan: a.end_of_plan_market_value,
      };
    });

    return [
      graphData,
      tableData,
      projection.data.probability_of_success,
      undefined,
    ];
  }, [projection, getPlatformAccountNameAndType]);

  const { mutateAsync: createFinancialPlan, isLoading: isMutatingPostPlan } =
    usePostGoalBasedFinancialPlan(auth);

  const { mutateAsync: patchGoalBasedFinancialPlan } =
    usePatchGoalBasedFinancialPlan(auth);

  const {
    data: selectedGoalBasedFinancialPlan,
    isLoading: isLoadingSinglePlan,
  } = useGetGoalBasedFinancialPlan(
    goalBasedFinancialPlanId !== undefined ? goalBasedFinancialPlanId : null,
    auth,
    false,
    {
      enabled: goalBasedFinancialPlanId !== undefined,
    }
  );

  const [showCreateFinancialPlanModal, setShowCreateFinancialPlanModal] =
    useState<boolean>(false);

  const [showEditFinancialPlanModal, setShowEditFinancialPlanModal] =
    useState<boolean>(false);

  const handleSetShowFinancialPlanModal = () => {
    trackEvent({
      name: "Client Click Create Plan Button",
      properties: {
        clientId,
        totalExistingPlans: completedPlans.length + drafts.length,
        isFirstPlan: completedPlans.length + drafts.length === 0,
        location: "dashboard",
      },
    });
    setShowCreateFinancialPlanModal(true);
  };
  const handleSetHideFinancialPlanModal = () =>
    setShowCreateFinancialPlanModal(false);

  const noCompletePlans = completedPlans.length <= 0;
  const someIncompletePlans = drafts.length > 0;
  const showDraftPlansDropdown = noCompletePlans && someIncompletePlans;

  const handleOnClickEditSelectedFinancialPlan = () => {
    if (
      selectedGoalBasedFinancialPlan?.goalBasedFinancialPlanId !== undefined
    ) {
      navigate(
        `${CreateUpdatePath}/${selectedGoalBasedFinancialPlan.goalBasedFinancialPlanId}`
      );
    }
  };

  const getViewButtonUrl = (): string => {
    if (
      selectedGoalBasedFinancialPlan?.goalBasedFinancialPlanId !== undefined
    ) {
      return `${CreateUpdatePath}/${selectedGoalBasedFinancialPlan?.goalBasedFinancialPlanId}`;
    }

    return "";
  };

  const onCreateFinancialPlan = async (name: string) => {
    const body = {
      owner: clientId,
      name,
    };

    // Track plan creation started
    trackEvent({
      name: "Client Start Plan Creation",
      properties: {
        clientId,
        planName: name,
        totalExistingPlans: completedPlans.length + drafts.length,
        isFirstPlan: completedPlans.length + drafts.length === 0,
      },
    });

    try {
      const newPlan = await createFinancialPlan({ body });

      setShowCreateFinancialPlanModal(false);

      notify("Financial plan created", "Success");

      // Track successful plan creation
      trackEvent({
        name: "Client Plan Created Successfully",
        properties: {
          clientId,
          planId: newPlan.goalBasedFinancialPlanId,
          planName: name,
        },
      });

      navigate(`${CreateUpdatePath}/${newPlan.goalBasedFinancialPlanId}`);
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error creating plan ${message ?? ""}`, "Error");
      } else {
        notify(
          "There was an error in creating a plan. Please try again.",
          "Error"
        );
      }
    }
  };

  const onEditGoalBasedFinancialPlan = async (name: string) => {
    if (!selectedGoalBasedFinancialPlan) {
      throw new Error("Financial plan not found");
    }
    const body = {
      name,
    };

    try {
      await patchGoalBasedFinancialPlan({
        goalBasedFinancialPlanId:
          selectedGoalBasedFinancialPlan.goalBasedFinancialPlanId,
        body,
      });

      setShowEditFinancialPlanModal(false);

      notify("Financial plan updated", "Success");
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        notify(`Error updating plan ${message ?? ""}`, "Error");
      } else {
        notify(
          "There was an error in updating a plan. Please try again.",
          "Error"
        );
      }
    }
  };

  const handleOnClickShowRenameFinancialPlanModal = () =>
    setShowEditFinancialPlanModal(true);

  const handleOnClickHideRenameFinancialPlanModal = () => {
    setShowEditFinancialPlanModal(false);
  };

  const options = [
    {
      label: "Rename",
      onClick: handleOnClickShowRenameFinancialPlanModal,
      isCloseOnClick: true,
    },
    {
      label: "Edit",
      onClick: handleOnClickEditSelectedFinancialPlan,
      isCloseOnClick: true,
    },
  ];

  const onSetFinancialPlan = useCallback(
    (id: GoalBasedFinancialPlanId) =>
      navigate(`/Client/${clientId}/${FinancialPlansPath}/Dashboard/${id}`),
    [clientId, navigate]
  );

  const handleOnSelectPlan = useCallback(
    (id: GoalBasedFinancialPlanId) => {
      const isDraftPlan = drafts.find(
        (draft) => draft.goalBasedFinancialPlanId === id
      );

      if (isDraftPlan) {
        navigate(
          `/Client/${clientId}/${FinancialPlansPath}/CreateUpdate/${id}`
        );
      } else {
        onSetFinancialPlan(id);
      }
    },
    [clientId, drafts, navigate, onSetFinancialPlan]
  );

  const showEmptyOverlay = [...completedPlans, ...drafts].length === 0;

  const onCloseUnderwaterAccountsModal = () =>
    setShowUnderwaterAccountsModal(false);

  if (projection.hasError) {
    return <div>Error</div>;
  }

  return (
    <BaseLayout disableScroll={showEmptyOverlay}>
      {showEmptyOverlay && (
        <div
          className={toClassName(styles.overlay, {
            [styles.overlay]: showEmptyOverlay,
          })}
        />
      )}

      <PageHero
        className={toClassName({
          [styles.pageHero]: !noCompletePlans,
          [styles.pageHeroNoPlans]: noCompletePlans,
        })}
        start={
          noCompletePlans ? (
            <div className={styles.heroTitleGap}>
              <TitleBlock
                title="Financial Plans"
                subTitle="Identify your goals and create a plan to achieve them."
              />

              <div className={styles.flexRowWithSpace1Gap}>
                <Button
                  text={"Create Plan"}
                  variant={"contained"}
                  buttonType={"primary"}
                  onClick={handleSetShowFinancialPlanModal}
                />

                {showDraftPlansDropdown &&
                  goalBasedFinancialPlanId !== undefined && (
                    <FinancialPlansDropdownMenu
                      goalBasedFinancialPlanId={goalBasedFinancialPlanId}
                      setGoalBasedFinancialPlanId={onSetFinancialPlan}
                      showCreateFinancialPlanModal={
                        handleSetShowFinancialPlanModal
                      }
                      onSelect={handleOnSelectPlan}
                      width={215}
                    />
                  )}
              </div>
            </div>
          ) : (
            <TitleBlock title="Financial Plans" />
          )
        }
        end={
          noCompletePlans ? (
            <div className={styles.flexRowWithSpace2Gap}>
              <HeroTooltipCard
                title="Set Goals"
                text="Track your financial goals — anything from buying a home to saving for a wedding."
                className={styles.heroCard}
              />

              <HeroTooltipCard
                title="Track income and expenses into retirement"
                text="Track your income, expenses, and savings to see if you're on track."
                className={styles.heroCard}
              />
            </div>
          ) : (
            <div className={styles.flexRowWithSpace2Gap}>
              <Button
                text={"Create Plan"}
                variant={"text"}
                buttonType={"primary"}
                onClick={handleSetShowFinancialPlanModal}
              />

              {goalBasedFinancialPlanId !== undefined && (
                <FinancialPlansDropdownMenu
                  goalBasedFinancialPlanId={goalBasedFinancialPlanId}
                  setGoalBasedFinancialPlanId={onSetFinancialPlan}
                  showCreateFinancialPlanModal={handleSetShowFinancialPlanModal}
                  onSelect={handleOnSelectPlan}
                />
              )}
            </div>
          )
        }
      />

      {isFetchingProjection && (
        <div className={styles.loader}>
          <LogoLoadingStill />
        </div>
      )}

      {showUnderwaterAccountsModal &&
        accountsData !== undefined &&
        underWaterAccountsDesc !== undefined && (
          <AlertModal
            onClose={onCloseUnderwaterAccountsModal}
            text={`Based on the income and expenses from your plan, your ${underWaterAccountsDesc} account${
              accountsData.length > 1 ? "s" : ""
            } will be underwater. This means that you may not have enough money to meet your goals. You may want to consider adjusting your plan.`}
          />
        )}

      {showCreateFinancialPlanModal && (
        <SimpleModalWithTextInput
          title={"New Plan"}
          text={"To get started, name your new plan."}
          label={"Plan Name"}
          defaultTextInputValue={""}
          confirmText="Continue"
          placeholder={"Enter a name for your financial plan"}
          onSave={onCreateFinancialPlan}
          closeModal={handleSetHideFinancialPlanModal}
          isDisabled={isMutatingPostPlan}
        />
      )}

      {showEditFinancialPlanModal && (
        <SimpleModalWithTextInput
          title={"Edit plan name"}
          label={"Plan Name"}
          confirmText="Save"
          defaultTextInputValue={selectedGoalBasedFinancialPlan?.name ?? ""}
          placeholder={"Enter a name for your financial plan"}
          onSave={onEditGoalBasedFinancialPlan}
          closeModal={handleOnClickHideRenameFinancialPlanModal}
          isDisabled={isMutatingPostPlan}
        />
      )}

      <div className={styles.contentContainer}>
        {accountsData !== undefined && underWaterAccountsDesc !== undefined && (
          <AlertInline
            className={styles.alert}
            message={`Based on the income and expenses from your plan, your ${underWaterAccountsDesc} account${
              accountsData.length > 1 ? "s" : ""
            } will be underwater.`}
            severity={"error"}
          />
        )}

        <div className={styles.heading}>
          <span className={styles.planNameWrapper}>
            <Text variant="title2" color="text">
              {selectedGoalBasedFinancialPlan?.name ?? "Your Financial Plan"}
            </Text>

            <Menu options={options}>
              {({ handleOpenMenu }) => (
                <IconButton
                  iconClassName={styles.attachmentMenuIcon}
                  onClick={handleOpenMenu}
                  IconComponent={MoreHorizIcon}
                />
              )}
            </Menu>
          </span>

          <Text variant="body" color="textSubtle">
            {getUpdatedAtString(selectedGoalBasedFinancialPlan?.updatedAt)}
          </Text>
        </div>

        <div className={styles.gap4}>
          <SuccessMeter score={Math.round((probabilityScore ?? 0) * 100)} />

          <GoalsTable
            selectedGoalBasedFinancialPlan={selectedGoalBasedFinancialPlan}
            isLoading={
              isLoadingSinglePlan && goalBasedFinancialPlanId !== undefined
            }
            viewButtonUrl={getViewButtonUrl()}
          />
        </div>

        <ProjectionGraph data={projectionData ?? []} />

        <FinancialPlansProjectionTable projections={accountsData ?? []} />
      </div>

      {isMutatingPostPlan && <LogoLoadingStill onTop />}
    </BaseLayout>
  );
};

export default FinancialPlansDashboard;
